import React, { useEffect } from "react";
import { SectionTitle, SectionTagline, SwitchInput } from "../components";
import { ProfilePaymentIcon } from "@components";
import { useForm } from "react-hook-form";
import usePaymentSettingsForm from "../hooks/usePaymentSettingsForm";
import { applyPaymentMethodRules } from "@utils";

const Ecommerce = () => {
  const {
    merchant,
    paymentSettings,
    paymentRequests,
    checkout,
    corporateCardSurchargePercent,
    klarnaAccountEmail,
    passOnCorporateCardFee,
    checkoutPassOnCorporateCardFee,
    submitPaymentSettings,
  } = usePaymentSettingsForm();
  const isAdyen = paymentSettings?.cardPaymentProvider === "Adyen";
  const showKlarna = isAdyen && !!klarnaAccountEmail;

  const availableMethods = [
    {
      id: "payByBank",
      name: "Pay by Bank",
      icon: "Bank",
      fieldName: "checkout.allowOpenBanking",
    },
    {
      id: "allowCards",
      name: "Personal / Corporate Card",
      icon: "Card",
      fieldName: "checkout.allowCard",
    },
    {
      id: "allowAmex",
      name: "American Express",
      icon: "Amex",
      fieldName: "checkout.allowAmex",
    },
    ...(showKlarna
      ? [
          {
            id: "klarna",
            name: "Klarna",
            icon: "Klarna",
            fieldName: "checkout.allowKlarna",
          },
        ]
      : []),
  ];

  const { getValues, setValue, control, reset } = useForm({
    defaultValues: {
      checkout,
    },
  });

  useEffect(() => {
    if (merchant) {
      reset({ checkout });
    }
  }, [merchant, paymentSettings]);

  if (!merchant) return null;

  const onInputChange = (name, value) => {
    setValue(name, value);
    const changedKey = name.replace("checkout.", "");
    const next = applyPaymentMethodRules(getValues().checkout, changedKey);
    setValue("checkout", next);
    onSubmit(getValues());
  };

  const onSubmit = async (values) => {
    await submitPaymentSettings({
      paymentRequests,
      checkout: values.checkout,
      corporateCardSurchargePercent,
      passOnCorporateCardFee,
      klarnaAccountEmail,
      checkoutPassOnCorporateCardFee,
    });
  };

  return (
    <div className="flex flex-col items-center py-4 px-5 overflow-auto">
      <form className="py-8 w-full max-w-4xl flex flex-col gap-y-8">
        <div className="flex flex-col gap-y-1">
          <SectionTitle>E-commerce</SectionTitle>
          <SectionTagline>
            Choose the payment methods available to e-commerce customers at
            checkout
          </SectionTagline>
        </div>
        <div className="w-full flex flex-col items-start">
          <div className="rounded-3xl bg-white shadow-[4px_4px_16px_0px_rgba(0,0,0,0.16)] py-4 px-6 w-full">
            {availableMethods.map((method) => (
              <div
                key={method.id}
                className="flex items-center justify-between py-2"
              >
                <div className="flex items-center gap-6">
                  <ProfilePaymentIcon paymentType={method.icon} />
                  <span className="text-sm font-medium text-zinc-900">
                    {method.name}
                  </span>
                </div>
                <SwitchInput
                  name={method.fieldName}
                  control={control}
                  disabled={method.disabled}
                  onChange={
                    method.disabled
                      ? undefined
                      : (val) => onInputChange(method.fieldName, val)
                  }
                />
              </div>
            ))}
          </div>
        </div>
      </form>
    </div>
  );
};

export default Ecommerce;
